import * as THREE from 'three';
import { buildCraftableModel, CRAFTABLE_IDS, CRAFTABLES, type CraftableId } from './craftables';
import { Inventory, FOOD_ACCEPT, type ItemId, type Stack } from './inventory';
import { cookResult, refineResult, smeltResult } from './consumables';
import { buildChestMesh } from './chest';
import { buildCampfireMesh } from './campfire';
import { nearestOnRay } from './aim';
import { LOCAL_PLAYER } from './identity';
import { BUILD, DEPLOY_HP, RESPAWN } from '../config';

// ============================ OXIDE PLACEMENT VOCABULARY (canonical terms) ============================
//   BUILDABLE  -- a blueprint structural PIECE placed with the Blueprint tool: foundations, walls, doorways,
//                 ceilings, stairs, the cupboard. Typed `PieceId` (build/catalog.ts), lives in the Builder.
//   DEPLOYABLE -- any standalone OBJECT you place from a hotbar slot: every CRAFTABLE (benches, furnaces, doors,
//                 windows, traps, baseclaim, ...) PLUS the legacy chest / bigchest / campfire. Typed `DeployId`.
//   CRAFTABLE  -- the subset of deployables defined in craftables.ts with a recipe + procedural model. `CraftableId`.
// So: every craftable is a deployable; chest/bigchest/campfire are deployables that predate the craftable registry
// (they keep their own ChestManager/CampfireManager for storage/cook logic, but are placed + ghosted + collided like
// any other deployable). DEPLOY_IDS is the full deployable set; use it instead of ad-hoc `=== 'chest' || ...` checks.
// =====================================================================================================
export type DeployId = CraftableId | 'chest' | 'bigchest' | 'campfire';
export const LEGACY_DEPLOY_IDS = new Set<DeployId>(['chest', 'bigchest', 'campfire']); // deployables that aren't CraftableIds

// A base claim. `author` is the permanent placer; `owner` is the current owner (can change / be cleared to null);
// `members` are authorized teammates with owner-level access. Access = you are the owner OR a member.
export interface Claim { x: number; y: number; z: number; g: THREE.Group; lock: 'wooden_lock' | 'keypad_lock' | null; author: string; owner: string | null; members: string[]; contents: Inventory; }

// Functional deployables wired in this pass: process stations (cook/smelt over time), filtered storage, and
// rain-filling water catchers. Each gets a record + a userData backref on its model (so a cursor/crosshair ray
// resolves it, like chests/campfires) and a collider via placed[] (added in deploy() before registration).
interface Station { id: CraftableId; x: number; y: number; z: number; g: THREE.Group; contents: Inventory; cols: number; rows: number; title: string; fuel: ItemId; burnSec: number; burnT: number; slots: number; on: boolean; recipe: (i: ItemId) => { into: ItemId; sec: number; need?: number } | null; progress: Map<ItemId, number>; light?: THREE.PointLight | THREE.SpotLight; netLit?: boolean; typed?: boolean; burnsIdle?: boolean; igniteT?: number; flickerSeed?: number; }
interface Storage { id: CraftableId; x: number; y: number; z: number; g: THREE.Group; contents: Inventory; cols: number; rows: number; title: string; accept?: ReadonlySet<ItemId>; owner: string; author: string; dropbox: boolean; }
interface Catcher { id: CraftableId; x: number; y: number; z: number; g: THREE.Group; water: number; capacity: number; rate: number; }

const STATION_IDS = new Set<CraftableId>(['furnace', 'large_furnace', 'oil_refinery', 'torch_sconce', 'hanging_lantern']);
const STORAGE_IDS = new Set<CraftableId>(['fridge', 'locker', 'drop_box', 'shelf']);
const CATCHER_IDS = new Set<CraftableId>(['water_catcher', 'large_water_catcher']);
const BED_IDS = new Set<CraftableId>(['sleeping_bag', 'bed']); // placed -> a respawn point you can spawn at

// A placed respawn point (sleeping bag / bed). Using it to respawn puts it on cooldown (anti spawn-camp).
export interface RespawnPoint { id: number; kind: 'bag' | 'bed'; x: number; y: number; z: number; owner: string; cooldownUntil: number; name?: string; }

// Furnace/large furnace do double duty: cook raw food AND smelt metal ore, same fire either way.
function furnaceResult(id: ItemId): { into: ItemId; sec: number; need?: number } | null {
  return cookResult(id) ?? smeltResult(id);
}
// FURNACE typed UI (owner 2026-07-09): both furnaces open with the campfire's Fuel / Input / Output layout
// -- just bigger. Slot roles MIRROR the server (stations.ts typedLayout): fuel slots first, then input,
// then output, contiguous. Furnace: 1 fuel + 2 inputs + 4 outputs (cap 7). Large furnace: 4 fuel + 4 inputs
// + 12 outputs (cap 20). Shared by the local sim (updateStations) + the UI render + main.ts.
interface TypedLayout { fuelSlots: readonly number[]; inputSlots: readonly number[]; outSlots: readonly number[]; cap: number; }
const range = (a: number, b: number): number[] => Array.from({ length: b - a }, (_, i) => a + i);
function typedLayout(nFuel: number, nInput: number, nOut: number): TypedLayout {
  return { fuelSlots: range(0, nFuel), inputSlots: range(nFuel, nFuel + nInput), outSlots: range(nFuel + nInput, nFuel + nInput + nOut), cap: nFuel + nInput + nOut };
}
export const FURNACE_LAYOUT = typedLayout(1, 2, 4);
export const LARGE_FURNACE_LAYOUT = typedLayout(4, 4, 12);
function typedSections(layout: TypedLayout): ReadonlyArray<{ label: string; slots: number[] }> {
  return [{ label: 'Fuel', slots: [...layout.fuelSlots] }, { label: 'Input', slots: [...layout.inputSlots] }, { label: 'Output', slots: [...layout.outSlots] }];
}
function typedSlotAccept(layout: TypedLayout): (slot: number, id: ItemId) => boolean {
  return (slot, id) => {
    if (layout.fuelSlots.includes(slot)) return id === 'wood';
    if (layout.inputSlots.includes(slot)) return !!furnaceResult(id);
    return false; // outputs are take-only
  };
}
export const FURNACE_SECTIONS = typedSections(FURNACE_LAYOUT);
export const furnaceSlotAccept = typedSlotAccept(FURNACE_LAYOUT);
export const LARGE_FURNACE_SECTIONS = typedSections(LARGE_FURNACE_LAYOUT);
export const largeFurnaceSlotAccept = typedSlotAccept(LARGE_FURNACE_LAYOUT);
function furnaceLayoutFor(id: CraftableId): TypedLayout { return id === 'large_furnace' ? LARGE_FURNACE_LAYOUT : FURNACE_LAYOUT; }

function stationCfg(id: CraftableId): { cols: number; rows: number; title: string; fuel: ItemId; burnSec: number; slots: number; recipe: (i: ItemId) => { into: ItemId; sec: number; need?: number } | null; typed?: boolean; cap?: number; burnsIdle?: boolean; defaultOff?: boolean } {
  if (id === 'oil_refinery') return { cols: 6, rows: 2, title: 'Oil Refinery  (fat -> light fuel, 10 metal -> refined metal; wood fuel)', fuel: 'wood', burnSec: 6, slots: 1, recipe: refineResult };
  if (id === 'large_furnace') return { cols: 8, rows: 3, title: 'Large Furnace  (wood + raw food or metal ore -> cooked food / metal; smelts 4 at once)', fuel: 'wood', burnSec: 5, slots: LARGE_FURNACE_LAYOUT.inputSlots.length, recipe: furnaceResult, typed: true, cap: LARGE_FURNACE_LAYOUT.cap };
  // Oil-burning LIGHT stations (owner spec 2026-07-12): a single fuel slot ("oil" = Light Fuel), no
  // recipes -- they burn purely for light, campfire-style (burnsIdle), and place OFF. Tap E toggles;
  // hold E opens the slot. Mirrored in server/src/stations.ts STATION_DEFS -- change BOTH.
  if (id === 'torch_sconce') return { cols: 1, rows: 1, title: 'Torch Sconce  (burns Light Fuel)', fuel: 'light_fuel', burnSec: 90, slots: 0, recipe: () => null, typed: true, cap: 1, burnsIdle: true, defaultOff: true };
  if (id === 'hanging_lantern') return { cols: 1, rows: 1, title: 'Hanging Lantern  (burns Light Fuel)', fuel: 'light_fuel', burnSec: 120, slots: 0, recipe: () => null, typed: true, cap: 1, burnsIdle: true, defaultOff: true };
  return { cols: 6, rows: 1, title: 'Furnace  (wood + raw food or metal ore -> cooked food / metal)', fuel: 'wood', burnSec: 6, slots: FURNACE_LAYOUT.inputSlots.length, recipe: furnaceResult, typed: true, cap: FURNACE_LAYOUT.cap };
}
// The oil lights' 1-slot UI: one "Oil" section, Light Fuel only.
export const SCONCE_SECTIONS: ReadonlyArray<{ label: string; slots: number[] }> = [{ label: 'Oil', slots: [0] }];
export function sconceSlotAccept(_slot: number, id: ItemId): boolean { return id === 'light_fuel'; }
function storageCfg(id: CraftableId): { cols: number; rows: number; title: string; accept?: ReadonlySet<ItemId>; dropbox: boolean } {
  if (id === 'fridge') return { cols: 8, rows: 4, title: 'Fridge  (food only)', accept: FOOD_ACCEPT, dropbox: false };
  if (id === 'drop_box') return { cols: 6, rows: 2, title: 'Drop Box', dropbox: true };
  if (id === 'shelf') return { cols: 6, rows: 1, title: 'Shelf', dropbox: false }; // wall-mounted 6-slot store (frees floor space)
  return { cols: 6, rows: 4, title: 'Locker', dropbox: false }; // locker = a plain medium store
}
function catcherCfg(id: CraftableId): { capacity: number; rate: number } {
  return id === 'large_water_catcher' ? { capacity: 300, rate: 16 } : { capacity: 120, rate: 8 };
}
import { obbOverlap, type Box, type BoxPool } from '../engine/collision';
import { placeRule } from './deployRules';

const WINDOW_IDS = new Set<CraftableId>(['wood_window', 'metal_window', 'glass_window', 'reinforced_window']);

// Placed CRAFTABLE deployables (benches, furnaces, doors, traps, ...). Each is the real craftables.ts model,
// dropped in the world with a footprint collider so the player can't walk through it and nothing else can be
// placed overlapping it. Placement is validated against: in-reach, a valid surface, no overlap with another
// deployable, and no clipping into a wall/built collider. (Rules per design: no overlap, no wall clip, no stack.)

// Footprint half-extents [hx,hy,hz] (metres) per craftable -> the collider + the overlap radius.
// Hero deployables (claim/benches/stations/storage/chests/campfire) carry the 1.15 art scale-up.
// GLB-backed entries (owner 2026-07-09: baseclaim/bench1-3/furnace/large_furnace/fridge/locker/bed/
// water_catcher/research_table/repair_table/large_water_catcher/oil_refinery) are MEASURED off the real
// fitted mesh in-game (Box3, half of each world-space dimension),
// not eyeballed -- the old procedural-era guesses were badly undersized for large_furnace especially (you
// could walk straight through it). Re-measure via F4 -> OBJECTS -> Buildables (or a live Box3 check) any
// time the owner re-scales one of these in buildable_resizers.json, or the collider drifts from the mesh
// again.
const FOOT: Partial<Record<DeployId, [number, number, number]>> = {
  chest: [0.35, 0.21, 0.23], bigchest: [0.48, 0.29, 0.32], campfire: [0.48, 0.23, 0.48], // legacy deployables, ghost + collide like the rest
  baseclaim: [0.86, 1.24, 0.71],
  bench1: [0.83, 0.52, 0.6], bench2: [1.0, 0.91, 0.5], bench3: [1.02, 1.09, 0.68],
  research_table: [0.96, 0.56, 0.68], repair_table: [0.96, 0.67, 0.63],
  furnace: [0.59, 0.72, 0.58], large_furnace: [1.6, 2.04, 1.43], oil_refinery: [1.18, 1.56, 0.95],
  sleeping_bag: [0.42, 0.12, 0.96], bed: [0.72, 0.54, 1.32],
  fridge: [0.46, 0.96, 0.38], locker: [0.33, 0.96, 0.37], drop_box: [0.31, 0.32, 0.22],
  // door leaves are THIN now (~0.05 deep, sunken in the doorway)
  wooden_door: [0.48, 1.0, 0.05], metal_door: [0.48, 1.0, 0.05], metal_double_door: [0.92, 1.0, 0.05], reinforced_door: [0.48, 1.0, 0.05],
  wood_window: [0.5, 0.5, 0.08], metal_window: [0.5, 0.5, 0.08], glass_window: [0.5, 0.5, 0.08], reinforced_window: [0.5, 0.5, 0.1],
  water_catcher: [0.76, 1.17, 0.51], large_water_catcher: [1.25, 1.55, 0.85],
  wooden_lock: [0.12, 0.16, 0.06], keypad_lock: [0.12, 0.16, 0.06],
  pistol_trap: [0.12, 0.5, 0.16], shotgun_trap: [0.12, 0.48, 0.42], flame_trap: [0.18, 0.42, 0.18],
  spikes: [0.4, 0.45, 0.4], mine: [0.25, 0.1, 0.25],
  // wall/ceiling furnishings: hz (depth off the wall) doubles as the mount standoff, hy as the hang drop
  torch_sconce: [0.12, 0.3, 0.18], shelf: [0.55, 0.24, 0.16], wood_sign: [0.47, 0.27, 0.05], hanging_lantern: [0.09, 0.24, 0.09],
  planter: [0.62, 0.21, 0.42],
};
const DEF: [number, number, number] = [0.4, 0.4, 0.4];
export function deployFoot(id: DeployId): [number, number, number] { return FOOT[id] ?? DEF; }

// Hammer-repair currency for a placeable: its LARGEST recipe line (a furnace repairs with stone, a
// locker with metal, a bag with cloth). Null for the legacy non-craftables (they repair via their own
// managers) or anything without a recipe.
export function deployRepairCost(id: DeployId): { item: ItemId; amount: number } | null {
  const def = CRAFTABLES.find((c) => c.id === id);
  if (!def || !def.recipe.items.length) return null;
  let bestItem: ItemId = def.recipe.items[0][0], bestAmt = def.recipe.items[0][1];
  for (const [item, amount] of def.recipe.items) if (amount > bestAmt) { bestItem = item; bestAmt = amount; }
  return { item: bestItem, amount: bestAmt };
}

// Deployable-vs-deployable overlap shrink: both boxes' XZ half-extents scale by this before the
// SAT, keeping deliberately tight packs legal (a bench beside a research table sits at 1.6m with
// 1.68m of summed extents) -- roughly as permissive as the old 0.82 max-extent-circle test, but
// yaw-aware so elongated items no longer block a whole circle around themselves.
const OVERLAP_SHRINK = 0.93;

interface Placed { id: CraftableId; x: number; y: number; z: number; yaw: number; hx: number; hy: number; hz: number; g: THREE.Group; host?: string; hp: number; maxHp: number; lastDamagedAt: number; }
// `host` = the placedId of the BUILT piece a wall/ceiling-mounted deployable is seated on. When that
// piece dies (demolish / raid / decay collapse), the builder's onPieceRemoved hook orphans the mount
// via orphanHost() below. hp/maxHp (owner spec 2026-07-12): every placeable is damageable (melee at
// full weapon damage, explosive ammo, explosions -- never normal bullets) and hammer-repairable; at 0
// it BREAKS (the item is destroyed, no drop) and only its CONTENTS spill to the ground.

interface Static { x: number; y: number; z: number; yaw: number; hx: number; hy: number; hz: number; }

// Local world save shape (game/persist.ts). Claim/station/storage/catcher extras are INDEX-ALIGNED
// with the deploy-order replay of `items`; showcase statics are boot-seeded, so they are not saved.
export interface SavedDeploy {
  items: Array<{ id: string; x: number; y: number; z: number; yaw: number; host?: string }>;
  windows: Array<{ id: string; x: number; y: number; z: number; yaw: number }>;
  claims: Array<{ lock: 'wooden_lock' | 'keypad_lock' | null; author: string; owner: string | null; members: string[]; contents: Array<Stack | null> }>;
  stations: Array<{ contents: Array<Stack | null> }>;
  storages: Array<{ owner: string; author: string; contents: Array<Stack | null> }>;
  catchers: Array<{ water: number }>;
}

export class DeployManager {
  readonly group = new THREE.Group();
  // Farming hook: a placed planter registers with the FarmingManager (main.ts wires this). A callback
  // rather than a direct import so deployables stays agnostic of the farming module (no cycle).
  onPlanterPlaced?: (x: number, y: number, z: number, g: THREE.Group) => void;
  private readonly placed: Placed[] = [];
  private readonly statics: Static[] = []; // showcase-base walls/floor + baseclaim: mesh already positioned, collider only
  private readonly claims: Claim[] = []; // baseclaim = a restricted medium chest (owner-gated when locked)
  private readonly beds: RespawnPoint[] = []; // placed sleeping bags / beds = respawn points
  private readonly bedMeshes = new Map<number, THREE.Group>(); // bed id -> its mesh (for pickup removal; kept out of the serialized record)
  private bedSeq = 0; // monotonic id for respawn points
  private readonly stations: Station[] = []; // furnace / large furnace / oil refinery
  private readonly storages: Storage[] = []; // fridge / locker / drop box
  private readonly catchers: Catcher[] = []; // water catchers
  private readonly windows: Array<{ id: CraftableId; x: number; y: number; z: number; yaw: number; g: THREE.Group; host?: string; hp: number; maxHp: number }> = []; // socket-mounted windows (hammer-removable, blow-out-able via own HP)

  // Add a pre-built, already-positioned model with a box collider (no craftable id). For the showcase base.
  addStatic(g: THREE.Group, x: number, y: number, z: number, yaw: number, hx: number, hy: number, hz: number): void {
    g.position.set(x, y, z); g.rotation.y = yaw;
    g.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh) { m.castShadow = true; m.receiveShadow = true; } });
    this.group.add(g);
    this.statics.push({ x, y: y + hy, z, yaw, hx, hy, hz });
  }

  // `owner` = who placed this (stamps claim/bed/storage ownership). Defaults to LOCAL_PLAYER for a LOCAL
  // placement (you); the network replay passes the true actor so a remote-placed claim/locker is owned by
  // its real placer on every client, not by whoever happens to be replaying it. This is the fix for
  // "everyone thinks they own everyone's base" (locks + add-user were no-ops because of the wrong owner).
  deploy(id: CraftableId, x: number, y: number, z: number, yaw: number, host?: string, owner: string = LOCAL_PLAYER): void {
    const g = buildCraftableModel(id);
    if (WINDOW_IDS.has(id)) {
      // Auto-fit a window into the wall opening: scale the frame to windowWidth x windowHeight + sit its centre at
      // the opening centre (x,y,z). No collider -- the wall opening already collides.
      const bb = new THREE.Box3().setFromObject(g); const size = new THREE.Vector3(), ctr = new THREE.Vector3(); bb.getSize(size); bb.getCenter(ctr);
      const sy = BUILD.windowHeight / Math.max(size.y, 0.01);
      g.scale.set(BUILD.windowWidth / Math.max(size.x, 0.01), sy, 1);
      g.position.set(x, y - ctr.y * sy, z); g.rotation.y = yaw;
      g.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh) { m.castShadow = true; m.receiveShadow = true; } });
      this.group.add(g);
      // Tracked so the hammer can pull it back off the socket (returned to inventory, socket freed), AND
      // so orphanHost can find it below -- a window used to carry no host at all, so demolishing its wall
      // left the frame floating in place forever (the "little diamond" leak the wall used to hide behind it).
      const whp = DEPLOY_HP[id] ?? 200;
      const wrec = { id, x, y, z, yaw, g, host, hp: whp, maxHp: whp };
      this.windows.push(wrec);
      g.traverse((o) => { o.userData.window = wrec; });
      return;
    }
    g.position.set(x, y, z); g.rotation.y = yaw;
    g.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh) { m.castShadow = true; m.receiveShadow = true; } });
    this.group.add(g);
    const [hx, hy, hz] = deployFoot(id);
    const hp = DEPLOY_HP[id] ?? 200;
    const prec: Placed = { id, x, y, z, yaw, hx, hy, hz, g, host, hp, maxHp: hp, lastDamagedAt: -Infinity };
    this.placed.push(prec);
    g.traverse((o) => { o.userData.deploy = prec; }); // aim backref: the hammer's hold-to-PICK-UP resolves this record
    if (id === 'baseclaim') { const rec: Claim = { x, y, z, g, lock: null, author: owner, owner, members: [], contents: new Inventory(18) }; this.claims.push(rec); g.traverse((o) => { o.userData.claim = rec; }); } // author+owner = the real placer; restricted 6x3 storage + aim backref
    else if (BED_IDS.has(id)) { const kind = id === 'bed' ? 'bed' : 'bag'; if (this.beds.filter((b) => b.kind === kind).length < RESPAWN.cap) { const rec: RespawnPoint = { id: ++this.bedSeq, kind, x, y, z, owner, cooldownUntil: 0, name: kind === 'bed' ? 'Bed' : 'Sleeping Bag' }; this.beds.push(rec); this.bedMeshes.set(rec.id, g); g.traverse((o) => { o.userData.bed = rec; }); } } // a respawn point (capped per kind) + aim backref
    else this.registerFunctional(id, x, y, z, g, owner); // furnace/storage/catcher interaction record + aim backref
  }

  // Wire a placed station/storage/catcher into its interaction list + tag its model for aim resolution.
  private registerFunctional(id: CraftableId, x: number, y: number, z: number, g: THREE.Group, owner: string): void {
    if (STATION_IDS.has(id)) {
      const c = stationCfg(id);
      const rec: Station = { id, x, y, z, g, contents: new Inventory(c.cap ?? c.cols * c.rows), cols: c.cols, rows: c.rows, title: c.title, fuel: c.fuel, burnSec: c.burnSec, burnT: 0, slots: c.slots, on: !c.defaultOff, recipe: c.recipe, progress: new Map(), typed: !!c.typed, burnsIdle: !!c.burnsIdle, flickerSeed: (x * 7 + z * 13) % 10 };
      if (id === 'torch_sconce' || id === 'hanging_lantern') {
        // A REAL warm point light (the models' emissive flame never actually lit anything) with the
        // handheld torch's flicker/ignite feel driven per-frame in updateStations. Small radius on
        // purpose: an indoor sconce lights a room, not the island.
        const sconce = id === 'torch_sconce';
        const pt = new THREE.PointLight(0xffa030, 0, sconce ? 14 : 12, 1.6);
        pt.position.set(0, sconce ? 0.38 : -0.05, sconce ? 0.26 : 0);
        pt.visible = false;
        g.add(pt);
        rec.light = pt;
      }
      if (id === 'furnace' || id === 'large_furnace') {
        // Owner spec: a DIRECTIONAL beam out of the stoke opening while burning (not a campfire point light).
        // A SpotLight at the mouth (opening faces +z toward the player), aimed forward + a touch down into the
        // floor. Both furnaces get the ON/OFF GLB swap + this beam; intensity/visibility follow the LIT state
        // in updateStations. The large furnace is bigger, so its beam sits higher + reaches a bit further.
        const big = id === 'large_furnace';
        const spot = new THREE.SpotLight(0xff7a2e, big ? 11 : 8, big ? 8 : 6.5, Math.PI / 4.2, 0.55, 1.3); // intensity fixed; toggled via .visible
        spot.position.set(0, big ? 0.7 : 0.5, big ? 0.4 : 0.26);
        spot.target.position.set(0, -0.25, big ? 3 : 2.4);
        spot.visible = false;
        g.add(spot); g.add(spot.target);
        rec.light = spot;
      }
      // oil_refinery gets no light (its burner is internal).
      this.stations.push(rec); g.traverse((o) => { o.userData.station = rec; });
    } else if (STORAGE_IDS.has(id)) {
      const c = storageCfg(id);
      const rec: Storage = { id, x, y, z, g, contents: new Inventory(c.cols * c.rows), cols: c.cols, rows: c.rows, title: c.title, accept: c.accept, owner, author: owner, dropbox: c.dropbox };
      this.storages.push(rec); g.traverse((o) => { o.userData.storage = rec; });
    } else if (CATCHER_IDS.has(id)) {
      const c = catcherCfg(id);
      const rec: Catcher = { id, x, y, z, g, water: 0, capacity: c.capacity, rate: c.rate };
      this.catchers.push(rec); g.traverse((o) => { o.userData.catcher = rec; });
    } else if (id === 'planter') this.onPlanterPlaced?.(x, y, z, g);
  }

  // The Base Claim nearest (px,pz) within range (or the one the ray is aimed at) -> the claim record (contents +
  // pos + lock + owner) for the E/open flow + the access gate.
  nearestClaim(px: number, pz: number, range: number): Claim | null {
    let best: Claim | null = null, bestSq = range * range;
    for (const c of this.claims) { const dx = c.x - px, dz = c.z - pz, sq = dx * dx + dz * dz; if (sq <= bestSq) { bestSq = sq; best = c; } }
    return best;
  }
  aimedClaim(raycaster: THREE.Raycaster): Claim | null {
    for (const h of raycaster.intersectObject(this.group, true)) {
      let o: THREE.Object3D | null = h.object;
      while (o) { if (o.userData.claim) return o.userData.claim as Claim; o = o.parent; }
    }
    return nearestOnRay(raycaster, this.claims.map((c) => ({ x: c.x, y: c.y + 0.6, z: c.z, rec: c })), 0.5);
  }

  // --- functional deployables (stations / storages / catchers): aim-first + nearest selectors ---
  aimedStation(rc: THREE.Raycaster): Station | null { return this.aimHit<Station>(rc, 'station') ?? nearestOnRay(rc, this.stations.map((s) => ({ x: s.x, y: s.y + 0.5, z: s.z, rec: s })), 0.5); }
  nearestStation(px: number, pz: number, range: number): Station | null { return this.near(this.stations, px, pz, range); }
  aimedStorage(rc: THREE.Raycaster): Storage | null { return this.aimHit<Storage>(rc, 'storage') ?? nearestOnRay(rc, this.storages.map((s) => ({ x: s.x, y: s.y + 0.5, z: s.z, rec: s })), 0.5); }
  nearestStorage(px: number, pz: number, range: number): Storage | null { return this.near(this.storages, px, pz, range); }
  aimedCatcher(rc: THREE.Raycaster): Catcher | null { return this.aimHit<Catcher>(rc, 'catcher') ?? nearestOnRay(rc, this.catchers.map((c) => ({ x: c.x, y: c.y + 0.5, z: c.z, rec: c })), 0.5); }
  nearestCatcher(px: number, pz: number, range: number): Catcher | null { return this.near(this.catchers, px, pz, range); }

  private aimHit<T>(rc: THREE.Raycaster, key: string): T | null {
    for (const h of rc.intersectObject(this.group, true)) {
      let o: THREE.Object3D | null = h.object;
      while (o) { const v = o.userData[key]; if (v) return v as T; o = o.parent; }
    }
    return null;
  }
  private near<T extends { x: number; z: number }>(list: T[], px: number, pz: number, range: number): T | null {
    let best: T | null = null, bestSq = range * range;
    for (const r of list) { const dx = r.x - px, dz = r.z - pz, sq = dx * dx + dz * dz; if (sq <= bestSq) { bestSq = sq; best = r; } }
    return best;
  }

  // The baseclaim covering (x,z) within its claim radius (for drop-box access gating), or null.
  claimAt(x: number, z: number, radius = BUILD.cupboardRadius): Claim | null { return this.near(this.claims, x, z, radius); }
  // The claim AT (x,z) (tight match -- the ownership ops target a specific claim by its placed position).
  claimByPos(x: number, z: number): Claim | null { return this.near(this.claims, x, z, 0.75); }
  // Every placed claim (the upkeep system iterates these each day to charge upkeep / drive decay).
  eachClaim(): readonly Claim[] { return this.claims; }

  // --- respawn points (sleeping bags / beds) ---
  // The player's respawn points (for the death-screen list). owner-scoped (MP-forward; always you in SP).
  respawnPoints(owner: string): readonly RespawnPoint[] { return this.beds.filter((b) => b.owner === owner); }
  // Spend a respawn point: if it exists + is off cooldown, set its cooldown (anti spawn-camp) and return where to spawn.
  useRespawn(id: number, owner: string, nowSec: number): { x: number; y: number; z: number } | null {
    const b = this.beds.find((p) => p.id === id && p.owner === owner);
    if (!b || nowSec < b.cooldownUntil) return null;
    b.cooldownUntil = nowSec + (b.kind === 'bed' ? RESPAWN.bedCooldownSec : RESPAWN.bagCooldownSec);
    return { x: b.x, y: b.y, z: b.z };
  }

  // --- bag/bed interaction (owner: hold-interact a placed bag to rename / pick up / give it) ---
  // The bed the cursor/crosshair is aimed at (precise mesh hit, then a forgiving ray-proximity), or null.
  aimedBed(rc: THREE.Raycaster): RespawnPoint | null { return this.aimHit<RespawnPoint>(rc, 'bed') ?? nearestOnRay(rc, this.beds.map((b) => ({ x: b.x, y: b.y + 0.2, z: b.z, rec: b })), 0.6); }
  // Rename a bag/bed (spawn-menu label). Falls back to the default name when cleared.
  renameBed(id: number, name: string): void { const b = this.beds.find((p) => p.id === id); if (b) b.name = name.trim().slice(0, 24) || (b.kind === 'bed' ? 'Bed' : 'Sleeping Bag'); }
  // Hand a bag/bed to another player (they become its owner, so it's THEIR spawn point now).
  giveBed(id: number, newOwner: string): boolean { const b = this.beds.find((p) => p.id === id); if (!b || !newOwner.trim()) return false; b.owner = newOwner.trim(); return true; }
  // Pick a bag/bed back up: drop it from the roster, remove its mesh, and return the item id to refund.
  removeBed(id: number): ItemId | null {
    const i = this.beds.findIndex((p) => p.id === id); if (i < 0) return null;
    const b = this.beds[i]; this.beds.splice(i, 1);
    const g = this.bedMeshes.get(id); if (g) { this.group.remove(g); g.traverse((o) => { const m = o as THREE.Mesh; if (m.geometry) m.geometry.dispose(); }); this.bedMeshes.delete(id); }
    return b.kind === 'bed' ? 'bed' : 'sleeping_bag';
  }
  bedById(id: number): RespawnPoint | null { return this.beds.find((p) => p.id === id) ?? null; }
  // POSITION-matched variants for the Backend: the local numeric bed id is not shared over the wire, so remote
  // rename/give/pickup ops address a bag by its world spot (identical on every client from the deployAt). ~1.2m
  // tolerance absorbs float drift. Used for BOTH local prediction (the owner) and remote replay (everyone else).
  private bedNearPos(x: number, z: number): RespawnPoint | null {
    let best: RespawnPoint | null = null, bestD = 1.44;
    for (const b of this.beds) { const dx = b.x - x, dz = b.z - z, d = dx * dx + dz * dz; if (d < bestD) { bestD = d; best = b; } }
    return best;
  }
  renameBedAt(x: number, z: number, name: string): void { const b = this.bedNearPos(x, z); if (b) b.name = name.trim().slice(0, 24) || (b.kind === 'bed' ? 'Bed' : 'Sleeping Bag'); }
  giveBedAt(x: number, z: number, owner: string): void { const b = this.bedNearPos(x, z); if (b && owner.trim()) b.owner = owner.trim(); }
  removeBedAt(x: number, z: number): ItemId | null { const b = this.bedNearPos(x, z); return b ? this.removeBed(b.id) : null; }

  // --- ownership (#7 base-claim wheel). The Backend authority calls these after validating reach; each returns
  // whether it changed anything so the caller can give the right feedback. `who` is the acting player. ---
  claimHasAccess(cl: Claim, who: string): boolean { return cl.owner === who || cl.members.includes(who); }
  // Claiming an unowned + unlocked base makes you its OWNER **and AUTHOR** -- authorship transfers with the claim
  // (owner ask #11: after your wife clears + reclaims, you lose author too; reclaim + clear her, you get it back).
  claimTakeOwnership(cl: Claim, who: string): boolean { if (cl.owner || cl.lock) return false; cl.owner = who; cl.author = who; return true; }
  // Relinquish -> fully unowned AND unauthored (author cleared), so a later claimer becomes the new author.
  claimClearOwnership(cl: Claim, who: string): boolean {
    if (cl.owner !== who) return false;
    cl.owner = null; cl.author = ''; cl.members = [];
    // RELINQUISHING ALSO DROPS THE LOCK, so the now-unowned claim can be re-taken. Without this, claimTakeOwnership's
    // `if (cl.owner || cl.lock) return false` guard left a cleared-but-locked claim permanently un-claimable -> nobody
    // could ever build there again (the reported reclaim-loses-all-privileges bug).
    if (cl.lock) { cl.lock = null; const lk = cl.g.children.find((o) => o.userData?.claimLock); if (lk) cl.g.remove(lk); }
    return true;
  }
  // A member gets OWNER-level access on this base, but NOT authorship (author stays whoever currently owns it).
  claimAddUser(cl: Claim, who: string, user: string): boolean { user = user.trim(); if (!user || !this.claimHasAccess(cl, who) || user === cl.owner || cl.members.includes(user)) return false; cl.members.push(user); return true; }
  claimRemoveUser(cl: Claim, who: string, user: string): boolean { if (!this.claimHasAccess(cl, who)) return false; const i = cl.members.indexOf(user); if (i < 0) return false; cl.members.splice(i, 1); return true; }

  // Is a placed deployable of `id` within range of (px,pz)? Used for proximity benches (e.g. the repair table).
  nearestId(id: CraftableId, px: number, pz: number, range: number): boolean {
    const r2 = range * range;
    for (const p of this.placed) { if (p.id !== id) continue; const dx = p.x - px, dz = p.z - pz; if (dx * dx + dz * dz <= r2) return true; }
    return false;
  }
  // The position of the nearest placed `id` within range (for a proximity-bench label anchor), or null.
  nearestIdPos(id: CraftableId, px: number, pz: number, range: number): { x: number; y: number; z: number } | null {
    let best: Placed | null = null, bestSq = range * range;
    for (const p of this.placed) { if (p.id !== id) continue; const dx = p.x - px, dz = p.z - pz, sq = dx * dx + dz * dz; if (sq <= bestSq) { bestSq = sq; best = p; } }
    return best ? { x: best.x, y: best.y, z: best.z } : null;
  }

  // The aimed workbench (bench1/2/3), for click-to-craft: interacting opens the craft menu at the bench.
  aimedBench(rc: THREE.Raycaster): { x: number; y: number; z: number; tier: number } | null {
    const benches = this.placed.filter((p) => p.id === 'bench1' || p.id === 'bench2' || p.id === 'bench3');
    const b = nearestOnRay(rc, benches.map((q) => ({ x: q.x, y: q.y + 0.6, z: q.z, rec: q })), 0.7);
    if (!b) return null;
    return { x: b.x, y: b.y, z: b.z, tier: b.id === 'bench3' ? 3 : b.id === 'bench2' ? 2 : 1 };
  }

  // The HIGHEST workbench tier (bench1/2/3 -> 1/2/3) within range of (px,pz); 0 if none in reach. Recipe tier
  // gating (craftMenu) checks this against Recipe.tier so a WB3 in range also satisfies tier-1/2 recipes.
  nearestBenchTier(px: number, pz: number, range: number): number {
    const r2 = range * range;
    let best = 0;
    for (const p of this.placed) {
      const t = p.id === 'bench1' ? 1 : p.id === 'bench2' ? 2 : p.id === 'bench3' ? 3 : 0;
      if (t <= best) continue;
      const dx = p.x - px, dz = p.z - pz;
      if (dx * dx + dz * dz <= r2) best = t;
    }
    return best;
  }

  // Drive a station's LIT visuals: its light (spotlight/ember) + the furnace's ON/OFF model swap. Called
  // every frame from updateStations (local sim) and applyStationState (MP netLit). Cheap: assigning an
  // unchanged .visible is a no-op, and the furnace model refs are cached on g.userData by craftables.ts.
  private setStationLit(s: Station, lit: boolean): void {
    if (s.light) s.light.visible = lit;
    if (s.id === 'furnace' || s.id === 'large_furnace') {
      const on = (s.g.userData as Record<string, THREE.Object3D | undefined>).furnaceOn;
      const off = (s.g.userData as Record<string, THREE.Object3D | undefined>).furnaceOff;
      if (on) on.visible = lit;
      if (off) off.visible = !lit;
    }
  }
  // Route a produced item into a TYPED furnace's OUTPUT slots (first-fit across them). Returns how many landed
  // (0 = every output slot is full / holds other items -> the item HOLDS 'done' and retries, like the campfire).
  private addFurnaceOutput(s: Station, id: ItemId, n: number): number {
    let left = n;
    for (const i of furnaceLayoutFor(s.id).outSlots) { if (left <= 0) break; left -= s.contents.addToSlot(i, id, left); }
    return n - left;
  }

  // Torch-style flame feel for the oil lights (sconce/lantern): an ignite ramp when lit plus a live
  // multi-sine flicker, mirroring the handheld torch's TORCH_FX language. Runs in BOTH the local-sim
  // and the MP netLit paths so a server-lit sconce still dances.
  private tickOilLight(s: Station, lit: boolean, dt: number): void {
    if (!s.burnsIdle || !s.light) return;
    if (!lit) { s.igniteT = 0; return; }
    s.igniteT = Math.min(0.45, (s.igniteT ?? 0) + dt);
    const ramp = (s.igniteT ?? 0) / 0.45; // ignite: the flame catches over ~half a second
    const t = performance.now() / 1000 + (s.flickerSeed ?? 0);
    const flick = 1 + 0.16 * Math.sin(t * 9.3) * Math.sin(t * 5.1) + 0.07 * Math.sin(t * 23.7);
    (s.light as THREE.PointLight).intensity = (s.id === 'torch_sconce' ? 30 : 24) * ramp * flick;
  }

  // Per-frame: process stations cook/smelt while fuelled; water catchers fill while it rains.
  updateStations(dt: number, rainIntensity: number): void {
    for (const s of this.stations) {
      if (s.netLit !== undefined) { this.setStationLit(s, s.netLit); this.tickOilLight(s, s.netLit, dt); continue; } // MP: the DO owns the sim; render its lit flag
      if (!s.on) { this.setStationLit(s, false); this.tickOilLight(s, false, dt); continue; } // #14: switched OFF -> no fuel burn, no smelting/cooking
      // Oil lights (sconce/lantern): no recipes -- burn purely for light while switched on + fuelled,
      // eating one Light Fuel per burnSec. The snuff is instant (out of oil / toggled off), the ignite
      // ramps -- flickers on, sputters out, just like the handheld torch (owner spec 2026-07-12).
      if (s.burnsIdle) {
        const lit = s.contents.count(s.fuel) > 0;
        this.setStationLit(s, lit);
        this.tickOilLight(s, lit, dt);
        if (lit) {
          s.burnT += dt;
          if (s.burnT >= s.burnSec) { s.burnT = 0; s.contents.take(s.fuel, 1); }
        } else s.burnT = 0;
        continue;
      }
      // TYPED (both furnaces): the raws come from the INPUT slots only; everything else reads the whole grid.
      let raws: ItemId[];
      if (s.typed) {
        raws = [];
        for (const i of furnaceLayoutFor(s.id).inputSlots) { const st = s.contents.stackAt(i); if (st && s.recipe(st.id) && !raws.includes(st.id)) raws.push(st.id); }
      } else raws = s.contents.entries().map((e) => e[0]).filter((id) => s.recipe(id));
      const active = raws.slice(0, s.slots);
      if (!active.length || s.contents.count(s.fuel) <= 0) { this.setStationLit(s, false); for (const k of [...s.progress.keys()]) if (!active.includes(k)) s.progress.delete(k); continue; }
      this.setStationLit(s, true); // burning: light on + (furnace) the glowing model
      s.burnT += dt;
      if (s.burnT >= s.burnSec) { s.burnT = 0; s.contents.take(s.fuel, 1); }
      for (const id of active) {
        const r = s.recipe(id)!;
        const need = r.need ?? 1; // inputs per output (refined metal eats 3 metal a batch)
        if (s.contents.count(id) < need) { s.progress.set(id, 0); continue; } // partial batch: wait, don't tick
        const t = (s.progress.get(id) ?? 0) + dt;
        if (t < r.sec) { s.progress.set(id, t); continue; }
        // Done: typed furnaces must have OUTPUT room before consuming the input (else hold at done + retry);
        // free grids always have room via first-fit.
        if (s.typed) {
          if (this.addFurnaceOutput(s, r.into, 1) > 0) { s.contents.take(id, need); s.progress.set(id, 0); }
          else s.progress.set(id, r.sec); // outputs full: keep it done, wait for the player to pull some out
        } else { s.progress.set(id, 0); if (s.contents.take(id, need) === need) s.contents.add(r.into, 1); }
      }
      for (const k of [...s.progress.keys()]) if (!active.includes(k)) s.progress.delete(k);
    }
    if (rainIntensity > 0) for (const c of this.catchers) c.water = Math.min(c.capacity, c.water + rainIntensity * c.rate * dt);
  }

  // Is a deployable of the SAME id already within `minDist` of (x,z)? Used to space out mines / spike traps so
  // they can't be packed wall-to-wall.
  nearSameId(id: CraftableId, x: number, z: number, minDist: number): boolean {
    const md2 = minDist * minDist;
    for (const p of this.placed) { if (p.id !== id) continue; const dx = p.x - x, dz = p.z - z; if (dx * dx + dz * dz < md2) return true; }
    return false;
  }

  // DESTRUCTION FADE + RUBBLE (owner 2026-07-11): a deployable killed by its host piece dying sinks,
  // shrinks and fades out over ~0.7s while a handful of rubble chips (coloured off the model's own
  // materials) tumble out -- instead of blinking out of existence. Ticked from update().
  private readonly fading: Array<{ g: THREE.Object3D; t: number }> = [];
  private readonly rubble: Array<{ m: THREE.Mesh; vx: number; vy: number; vz: number; t: number }> = [];
  private fadeOut(g: THREE.Object3D): void {
    this.fading.push({ g, t: 0 });
    let col = 0x8a8478; // sample a colour from the model's first mesh material for the chips
    g.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh && col === 0x8a8478) { const mat = (Array.isArray(m.material) ? m.material[0] : m.material) as THREE.MeshStandardMaterial; if (mat && mat.color) col = mat.color.getHex(); } });
    const wp = new THREE.Vector3(); g.getWorldPosition(wp);
    for (let i = 0; i < 7; i++) {
      const size = 0.05 + Math.random() * 0.09;
      const chip = new THREE.Mesh(new THREE.BoxGeometry(size, size, size), new THREE.MeshLambertMaterial({ color: col, flatShading: true }));
      chip.position.set(wp.x + (Math.random() - 0.5) * 0.5, wp.y + 0.3 + Math.random() * 0.6, wp.z + (Math.random() - 0.5) * 0.5);
      chip.rotation.set(Math.random() * 3, Math.random() * 3, Math.random() * 3);
      this.group.add(chip);
      this.rubble.push({ m: chip, vx: (Math.random() - 0.5) * 2.4, vy: 1.6 + Math.random() * 1.6, vz: (Math.random() - 0.5) * 2.4, t: 0 });
    }
  }
  tickDestroyFx(dt: number): void {
    for (let i = this.fading.length - 1; i >= 0; i--) {
      const f = this.fading[i]; f.t += dt;
      const k = Math.min(1, f.t / 0.7);
      f.g.scale.setScalar(Math.max(0.001, 1 - k));
      f.g.position.y -= dt * 0.6;
      if (k >= 1) { this.group.remove(f.g); f.g.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh) m.geometry.dispose(); }); this.fading.splice(i, 1); }
    }
    for (let i = this.rubble.length - 1; i >= 0; i--) {
      const r = this.rubble[i]; r.t += dt;
      r.vy -= 9.8 * dt;
      r.m.position.x += r.vx * dt; r.m.position.y += r.vy * dt; r.m.position.z += r.vz * dt;
      r.m.rotation.x += dt * 4; r.m.rotation.z += dt * 3;
      if (r.t > 1.4) { this.group.remove(r.m); r.m.geometry.dispose(); (r.m.material as THREE.Material).dispose(); this.rubble.splice(i, 1); }
    }
  }

  // Kill ONE placed deployable by index: splice its record, sweep every functional mirror that points at
  // its model (station/storage/catcher/bed/claim), fade the model, and return what should SPILL. Owner
  // spec 2026-07-12: the ITEM BREAKS -- it is never part of the loot; only stored CONTENTS drop. The one
  // shared death path for cascades (host wall / foundation gone) AND direct HP kills (melee/explosive).
  private destroyPlacedIndex(i: number): { id: CraftableId; x: number; y: number; z: number; loot: Array<{ id: ItemId; amount: number }> } {
    const p = this.placed[i];
    this.placed.splice(i, 1);
    const loot: Array<{ id: ItemId; amount: number }> = [];
    const si = this.stations.findIndex((st) => st.g === p.g);
    if (si >= 0) { for (const [it, n] of this.stations[si].contents.entries()) loot.push({ id: it, amount: n }); this.stations.splice(si, 1); }
    const ti = this.storages.findIndex((st) => st.g === p.g);
    if (ti >= 0) { for (const [it, n] of this.storages[ti].contents.entries()) loot.push({ id: it, amount: n }); this.storages.splice(ti, 1); }
    const ci = this.catchers.findIndex((ct) => ct.g === p.g);
    if (ci >= 0) this.catchers.splice(ci, 1); // stored water evaporates, not loot
    // A dead bed/bag stops being a respawn point (this leaked before: the ghost spawn survived the bed).
    for (const [bedId, mesh] of this.bedMeshes) {
      if (mesh !== p.g) continue;
      const bi = this.beds.findIndex((b) => b.id === bedId);
      if (bi >= 0) this.beds.splice(bi, 1);
      this.bedMeshes.delete(bedId);
      break;
    }
    // The base claim record dies with its totem; its stocked upkeep spills.
    const li = this.claims.findIndex((c) => c.g === p.g);
    if (li >= 0) { for (const [it, n] of this.claims[li].contents.entries()) loot.push({ id: it, amount: n }); this.claims.splice(li, 1); }
    this.fadeOut(p.g);
    return { id: p.id, x: p.x, y: p.y, z: p.z, loot };
  }

  // Everything STANDING on a destroyed foundation/ceiling dies with it (owner spec) -- INCLUDING a base
  // claim (a foundation break kills the TC too, per 2026-07-12). Items break; contents spill back to the
  // caller (main bags them). xz window + a y band above the slab.
  destroyStandingNear(x: number, y: number, z: number, r: number): Array<{ id: CraftableId; x: number; y: number; z: number; loot: Array<{ id: ItemId; amount: number }> }> {
    const out: Array<{ id: CraftableId; x: number; y: number; z: number; loot: Array<{ id: ItemId; amount: number }> }> = [];
    for (let i = this.placed.length - 1; i >= 0; i--) {
      const p = this.placed[i];
      if (Math.abs(p.x - x) > r || Math.abs(p.z - z) > r) continue;
      if (p.y < y - 0.6 || p.y > y + 1.6) continue;
      out.push(this.destroyPlacedIndex(i));
    }
    return out;
  }

  // Direct damage to the nearest placeable around a melee/blast impact point (chest.damageAt pattern).
  // Normal bullets never call this (owner spec: guns hurt players + barrels only). Returns the hit record
  // (died=false) or the death spill (died=true), or null if nothing was in range. Windows are tested too:
  // they have no collider, so the caller can't rely on a physical impact -- proximity to the pane counts.
  damageNear(x: number, y: number, z: number, dmg: number, range = 1.4): { id: CraftableId; x: number; y: number; z: number; died: boolean; loot: Array<{ id: ItemId; amount: number }>; wasWindow?: boolean } | null {
    let best = -1, bestSq = range * range, bestWin = -1;
    for (let i = 0; i < this.placed.length; i++) {
      const p = this.placed[i];
      const dx = p.x - x, dy = (p.y + p.hy) - y, dz = p.z - z;
      const sq = dx * dx + dy * dy + dz * dz;
      if (sq <= bestSq) { bestSq = sq; best = i; bestWin = -1; }
    }
    for (let i = 0; i < this.windows.length; i++) {
      const w = this.windows[i];
      const dx = w.x - x, dy = w.y - y, dz = w.z - z;
      const sq = dx * dx + dy * dy + dz * dz;
      if (sq <= bestSq) { bestSq = sq; bestWin = i; best = -1; }
    }
    if (bestWin >= 0) {
      const w = this.windows[bestWin];
      w.hp -= dmg;
      if (w.hp > 0) return { id: w.id, x: w.x, y: w.y, z: w.z, died: false, loot: [] };
      this.windows.splice(bestWin, 1);
      this.fadeOut(w.g);
      return { id: w.id, x: w.x, y: w.y, z: w.z, died: true, loot: [], wasWindow: true }; // blown out: socket freed by the caller
    }
    if (best < 0) return null;
    const p = this.placed[best];
    p.hp -= dmg;
    p.lastDamagedAt = performance.now() / 1000;
    if (p.hp > 0) return { id: p.id, x: p.x, y: p.y, z: p.z, died: false, loot: [] };
    return { ...this.destroyPlacedIndex(best), died: true };
  }

  // Blast damage to every placeable + window in radius (linear falloff, same shape as builder.explodeAt
  // splash). Explosions of BOTH scopes hit deployables -- a C4 splash kills the boxes behind the wall,
  // and breach rounds are the door/window/placeable raid path. Returns the deaths' spills.
  damageSplash(x: number, y: number, z: number, radius: number, dmg: number): Array<{ id: CraftableId; x: number; y: number; z: number; loot: Array<{ id: ItemId; amount: number }>; wasWindow?: boolean }> {
    const out: Array<{ id: CraftableId; x: number; y: number; z: number; loot: Array<{ id: ItemId; amount: number }>; wasWindow?: boolean }> = [];
    const r2 = radius * radius;
    const now = performance.now() / 1000;
    for (let i = this.placed.length - 1; i >= 0; i--) {
      const p = this.placed[i];
      const dx = p.x - x, dy = (p.y + p.hy) - y, dz = p.z - z;
      const sq = dx * dx + dy * dy + dz * dz;
      if (sq > r2) continue;
      const applied = dmg * (1 - Math.sqrt(sq) / radius);
      if (applied <= 0.5) continue;
      p.hp -= applied;
      p.lastDamagedAt = now;
      if (p.hp <= 0) out.push(this.destroyPlacedIndex(i));
    }
    for (let i = this.windows.length - 1; i >= 0; i--) {
      const w = this.windows[i];
      const dx = w.x - x, dy = w.y - y, dz = w.z - z;
      const sq = dx * dx + dy * dy + dz * dz;
      if (sq > r2) continue;
      const applied = dmg * (1 - Math.sqrt(sq) / radius);
      if (applied <= 0.5) continue;
      w.hp -= applied;
      if (w.hp <= 0) {
        this.windows.splice(i, 1);
        this.fadeOut(w.g);
        out.push({ id: w.id, x: w.x, y: w.y, z: w.z, loot: [], wasWindow: true });
      }
    }
    return out;
  }

  // HAMMER REPAIR for placeables (owner spec 2026-07-12: every placeable has HP, all repairable). Same
  // contract as builder.repairTarget: nearest damaged record in reach, locked out repairCooldownSec after
  // damage, spends the item's PRIMARY craft ingredient (its largest recipe line) proportионally --
  // repairPerHit material buys repairPerHit/full-cost of the HP pool back. Returns what it spent.
  repairNear(x: number, y: number, z: number, canAfford: (item: ItemId, amount: number) => boolean, spend: (item: ItemId, amount: number) => void, range = 2.5): { id: CraftableId; spent: { item: ItemId; amount: number } } | null {
    const nowS = performance.now() / 1000;
    let best: Placed | null = null, bestSq = range * range;
    for (const p of this.placed) {
      if (p.hp >= p.maxHp) continue;
      const dx = p.x - x, dy = (p.y + p.hy) - y, dz = p.z - z;
      const sq = dx * dx + dy * dy + dz * dz;
      if (sq <= bestSq) { bestSq = sq; best = p; }
    }
    if (!best) return null;
    if (nowS - best.lastDamagedAt < BUILD.repairCooldownSec) return null; // no combat-repair
    const cost = deployRepairCost(best.id);
    if (!cost) return null;
    const missingFrac = (best.maxHp - best.hp) / best.maxHp;
    const fullRepair = Math.max(1, Math.ceil(cost.amount * 0.5)); // full heal = half the item's primary craft line
    const amount = Math.max(1, Math.min(BUILD.repairPerHit, Math.ceil(fullRepair * missingFrac)));
    if (!canAfford(cost.item, amount)) return null;
    spend(cost.item, amount);
    best.hp = Math.min(best.maxHp, best.hp + best.maxHp * (amount / fullRepair));
    return { id: best.id, spent: { item: cost.item, amount } };
  }

  // The HP readout for the aim/hammer HUD: nearest placeable to the point, if any.
  deployHpNear(x: number, y: number, z: number, range = 2.5): { id: CraftableId; hp: number; max: number } | null {
    let best: Placed | null = null, bestSq = range * range;
    for (const p of this.placed) {
      const dx = p.x - x, dy = (p.y + p.hy) - y, dz = p.z - z;
      const sq = dx * dx + dy * dy + dz * dz;
      if (sq <= bestSq) { bestSq = sq; best = p; }
    }
    return best ? { id: best.id, hp: Math.max(0, Math.ceil(best.hp)), max: best.maxHp } : null;
  }

  private readonly _lampTmp = new THREE.Vector3();

  // STATUS LAMP on a base-claim totem: green = stocked, yellow = low/grace, red = decaying. A small
  // emissive sphere on the FRONT-TOP edge of the model (owner ask), created lazily and recoloured in
  // place (main drives it off upkeep). BUG FIX 2026-07-11: the old code hid the lamp and re-measured the
  // bounding box every tick -- but Box3.setFromObject ignores `visible`, so the box included the lamp's
  // own sphere and the seat ratcheted ~0.16 higher on every recolor (the "climbing light"). Measure with
  // the lamp DETACHED, and only re-measure when the model subtree actually changed (the async GLB swap).
  setClaimStatusColor(cl: Claim, hex: number): void {
    let lamp = cl.g.userData.statusLamp as THREE.Mesh | undefined;
    if (!lamp) {
      lamp = new THREE.Mesh(new THREE.SphereGeometry(0.075, 10, 8), new THREE.MeshBasicMaterial({ color: hex }));
      cl.g.userData.statusLamp = lamp;
    }
    if (cl.g.userData.lampSeatRoot !== cl.g.children[0]) { // fresh model (first call / GLB swap): re-seat
      lamp.removeFromParent();
      const box = new THREE.Box3().setFromObject(cl.g); // lamp detached = clean measure
      cl.g.getWorldPosition(this._lampTmp);
      const topY = Math.max(0.9, box.max.y - this._lampTmp.y);
      const frontZ = Math.max(0.2, box.max.z - this._lampTmp.z); // local +z = the front (lock emblem side)
      lamp.position.set(0, topY - 0.08, frontZ - 0.02); // tucked on the front-top edge, proud of the face
      cl.g.userData.lampSeatRoot = cl.g.children[0] ?? null;
    }
    if (!lamp.parent) cl.g.add(lamp);
    (lamp.material as THREE.MeshBasicMaterial).color.setHex(hex);
  }

  // Attach a lock to the nearest unlocked Base Claim in range. Returns true if one took it.
  lockNearestClaim(px: number, pz: number, range: number, kind: 'wooden_lock' | 'keypad_lock'): boolean {
    let best: typeof this.claims[number] | null = null, bestSq = range * range;
    for (const c of this.claims) { if (c.lock) continue; const dx = c.x - px, dz = c.z - pz, sq = dx * dx + dz * dz; if (sq <= bestSq) { bestSq = sq; best = c; } }
    if (!best) return false;
    this.attachClaimLock(best, kind);
    return true;
  }

  // The lock model on a claim totem, by the front emblem (shared by live locking + the save restore).
  private attachClaimLock(c: Claim, kind: 'wooden_lock' | 'keypad_lock'): void {
    const lk = buildCraftableModel(kind); lk.scale.setScalar(0.8);
    lk.position.set(0, 0.6, 0.34); lk.traverse((o) => { o.castShadow = true; });
    lk.userData.claimLock = true; // tagged so claimClearOwnership can remove the mesh when the claim is relinquished
    c.g.add(lk); c.lock = kind;
  }

  // --- LOCAL WORLD SAVE (game/persist.ts). serialize snapshots every placed deployable in DEPLOY
  // ORDER plus the per-record state a bare deploy() can't recreate (claim ownership/locks, storage
  // owners, contents, catcher water). deserialize REPLAYS deploy() for each (colliders, backrefs,
  // the planter hook and functional records all regenerate), then restores that state BY INDEX:
  // replay order matches save order, so the k-th claim/station/storage/catcher lines up exactly. ---
  serialize(): SavedDeploy {
    return {
      items: this.placed.map((p) => ({ id: p.id, x: p.x, y: p.y, z: p.z, yaw: p.yaw, host: p.host })),
      windows: this.windows.map((w) => ({ id: w.id, x: w.x, y: w.y, z: w.z, yaw: w.yaw })),
      claims: this.claims.map((c) => ({ lock: c.lock, author: c.author, owner: c.owner, members: c.members.slice(), contents: c.contents.serializeSlots() })),
      stations: this.stations.map((s) => ({ contents: s.contents.serializeSlots() })),
      storages: this.storages.map((s) => ({ owner: s.owner, author: s.author, contents: s.contents.serializeSlots() })),
      catchers: this.catchers.map((c) => ({ water: c.water })),
    };
  }

  deserialize(data: SavedDeploy, remapHost: (old: string) => string | undefined): void {
    const known = (id: string): id is CraftableId => CRAFTABLE_IDS.has(id as CraftableId);
    // A mount whose host piece failed to replay deploys hostless (it just won't orphan-drop later).
    for (const it of data.items) if (known(it.id)) this.deploy(it.id, it.x, it.y, it.z, it.yaw, it.host ? remapHost(it.host) : undefined);
    for (const w of data.windows) if (known(w.id)) this.deploy(w.id, w.x, w.y, w.z, w.yaw);
    data.claims.forEach((s, i) => {
      const c = this.claims[i]; if (!c) return;
      c.author = s.author; c.owner = s.owner; c.members = s.members.slice();
      c.contents.deserializeSlots(s.contents);
      if (s.lock && !c.lock) this.attachClaimLock(c, s.lock);
    });
    data.stations.forEach((s, i) => this.stations[i]?.contents.deserializeSlots(s.contents));
    data.storages.forEach((s, i) => {
      const r = this.storages[i]; if (!r) return;
      r.owner = s.owner; r.author = s.author; r.contents.deserializeSlots(s.contents);
    });
    data.catchers.forEach((s, i) => { const c = this.catchers[i]; if (c) c.water = Math.min(c.capacity, Math.max(0, s.water)); });
  }

  // Would a new placement at (x,y,z,yaw) overlap an existing deployable? Real oriented boxes now:
  // Y bands first (a trap mounted on the wall ABOVE a furnace is different vertical space), then a
  // 2D SAT on the two yaw'd rectangles. Half-extents shrink by OVERLAP_SHRINK so intentionally
  // tight furniture packing still reads legal -- the old test allowed ~0.82 of the summed
  // max-extent circles, and packed bases (a bench right beside a research table) depend on
  // near-flush placement. XZ-distance prune keeps the per-ghost-frame cost flat.
  overlaps(id: CraftableId, x: number, y: number, z: number, yaw = 0): boolean {
    const [hx, hy, hz] = deployFoot(id);
    const sx = hx * OVERLAP_SHRINK, sz = hz * OVERLAP_SHRINK;
    const sy = Math.max(0.02, hy - 0.02); // hair of Y slack: stacked bands that merely touch pass
    const cy = y + hy;
    for (const p of this.placed) {
      const dx = p.x - x, dz = p.z - z;
      const rr = sx + sz + p.hx + p.hz; // circumradius over-bound: cheap prune before the SAT
      if (dx * dx + dz * dz > rr * rr) continue;
      if (obbOverlap(x, cy, z, sx, sy, sz, yaw, p.x, p.y + p.hy, p.z, p.hx * OVERLAP_SHRINK, p.hy, p.hz * OVERLAP_SHRINK, p.yaw)) return true;
    }
    return false;
  }

  // --- HAMMER PICK-UP (owner spec 2026-07-12): hold-hammer on a placeable returns the ITEM to the
  // inventory. Gates: never the heavies below (destroy them instead), storage/stations must be EMPTY,
  // claim access is the caller's job (main gates like every other claim-covered act). Beds keep their
  // menu pickup too; this just makes the hammer work on everything uniformly. ---
  private static readonly NEVER_PICKUP = new Set<CraftableId>(['baseclaim', 'oil_refinery', 'large_water_catcher', 'large_furnace']);

  // The placed deployable under the aim ray (its model carries a userData.deploy backref), or null.
  aimedDeploy(rc: THREE.Raycaster): { id: CraftableId; x: number; y: number; z: number } | null {
    for (const h of rc.intersectObject(this.group, true)) {
      let o: THREE.Object3D | null = h.object;
      while (o) { if (o.userData.deploy) return o.userData.deploy as Placed; o = o.parent; }
    }
    return null;
  }

  // Why this record can't be picked up right now (a HUD-ready reason), or null = pickup allowed.
  pickupDeny(rec: { id: CraftableId; x: number; y: number; z: number }): string | null {
    const p = this.placed.find((q) => q.x === rec.x && q.y === rec.y && q.z === rec.z && q.id === rec.id);
    if (!p) return 'Nothing to pick up';
    if (DeployManager.NEVER_PICKUP.has(p.id)) return 'Too heavy — destroy it instead';
    const st = this.stations.find((s) => s.g === p.g);
    if (st && st.contents.entries().length) return 'Must be empty';
    const sto = this.storages.find((s) => s.g === p.g);
    if (sto && sto.contents.entries().length) return 'Must be empty';
    return null;
  }

  // Remove the placed deployable nearest (x,y,z) and hand back its item id -- the actual pickup.
  // The bedPickup pattern: SP + the MP actor apply through the backend intent; remote clients apply
  // the relayed deployRemoveAt op through this same method (fail-soft null if their state disagrees).
  pickupAt(x: number, y: number, z: number): CraftableId | null {
    let best = -1, bestSq = 1.0;
    for (let i = 0; i < this.placed.length; i++) {
      const p = this.placed[i];
      const dx = p.x - x, dy = p.y - y, dz = p.z - z;
      const sq = dx * dx + dy * dy + dz * dz;
      if (sq <= bestSq) { bestSq = sq; best = i; }
    }
    if (best < 0) return null;
    const p = this.placed[best];
    if (this.pickupDeny(p)) return null;
    this.placed.splice(best, 1);
    const si = this.stations.findIndex((s) => s.g === p.g); if (si >= 0) this.stations.splice(si, 1);
    const ti = this.storages.findIndex((s) => s.g === p.g); if (ti >= 0) this.storages.splice(ti, 1);
    const ci = this.catchers.findIndex((c) => c.g === p.g); if (ci >= 0) this.catchers.splice(ci, 1);
    for (const [bedId, mesh] of this.bedMeshes) {
      if (mesh !== p.g) continue;
      const bi = this.beds.findIndex((b) => b.id === bedId);
      if (bi >= 0) this.beds.splice(bi, 1);
      this.bedMeshes.delete(bedId);
      break;
    }
    this.group.remove(p.g); // picked up, not destroyed: no fade, no rubble (geometry stays with the GLB cache)
    return p.id;
  }

  // The mounted window the aim ray points at (within pick range), or null. Used by the hammer's
  // Remove mode to pull a window back off its socket and into the inventory.
  aimedWindow(rc: THREE.Raycaster): { id: CraftableId; x: number; y: number; z: number; yaw: number; g: THREE.Group } | null {
    for (const h of rc.intersectObject(this.group, true)) {
      let o: THREE.Object3D | null = h.object;
      while (o) { if (o.userData.window) return o.userData.window as { id: CraftableId; x: number; y: number; z: number; yaw: number; g: THREE.Group }; o = o.parent; }
    }
    return null;
  }

  // Remove a tracked window (hammer pull-off): drops the model, returns true if it was ours.
  removeWindow(rec: { g: THREE.Group }): boolean {
    const i = this.windows.findIndex((w) => w.g === rec.g);
    if (i < 0) return false;
    this.group.remove(this.windows[i].g);
    this.windows.splice(i, 1);
    return true;
  }

  // Orphan every deployable MOUNTED on the given built piece (its wall/ceiling just died). Owner spec
  // 2026-07-12: the mounted item BREAKS with its wall -- no item drop; only stored contents spill.
  // Windows are swept too (they used to carry no host at all, so demolishing their wall left the frame
  // floating in place forever -- the "little diamond" marker leak); each freed one is reported back so
  // the caller can unmark its wall socket (builder.unmarkWindowOccupied, wired in main.ts).
  orphanHost(placedId: string): Array<{ id: CraftableId; x: number; y: number; z: number; loot: Array<{ id: ItemId; amount: number }>; wasWindow?: boolean }> {
    const out: Array<{ id: CraftableId; x: number; y: number; z: number; loot: Array<{ id: ItemId; amount: number }>; wasWindow?: boolean }> = [];
    for (let i = this.placed.length - 1; i >= 0; i--) {
      if (this.placed[i].host !== placedId) continue;
      out.push(this.destroyPlacedIndex(i));
    }
    for (let i = this.windows.length - 1; i >= 0; i--) {
      const w = this.windows[i];
      if (w.host !== placedId) continue;
      this.windows.splice(i, 1);
      this.fadeOut(w.g);
      out.push({ id: w.id, x: w.x, y: w.y, z: w.z, loot: [], wasWindow: true }); // the pane breaks with the wall
    }
    return out;
  }

  // Append every placed deployable as a world collider (so the player is blocked by it like a wall/chest).
  collectColliders(out: Box[], pool: BoxPool): void {
    for (const p of this.placed) out.push(pool.take(p.x, p.y + p.hy, p.z, p.hx, p.hy, p.hz, p.yaw));
    for (const s of this.statics) out.push(pool.take(s.x, s.y, s.z, s.hx, s.hy, s.hz, s.yaw));
  }
}

// How much each half-extent deflates for the clip test: covers roughen()'s ~3cm surface wander on
// the raycast hit point plus float noise, so mounts seated exactly their own extent (+2cm) off a
// host face -- and furniture parked flush against a wall -- stay legal.
const CLIP_DEFLATE = 0.04;
// Lift the box bottom off the seat surface: the placement ray lands on roughened geometry that can
// sit BELOW the host's collider top, and the item must not read as clipping the floor it stands on.
const CLIP_SEAT_LIFT = 0.1;

// Does the deployable's ORIENTED BOX at (x,y,z,yaw) clip any wall/built collider? Full-height OBB
// vs each box (Y interval + 2D SAT) instead of the old 9 ankle-height point samples -- a locker can
// no longer sink its upper body into a wall the samples never reached. Top-anchored mounts (the
// hanging lantern) are centred on their seat point rather than standing on it, matching how the
// mount standoff places them under a ceiling. XZ prune before the SAT keeps ghost frames flat.
export function footprintClips(id: DeployId, x: number, y: number, z: number, yaw: number, boxes: ReadonlyArray<Box>): boolean {
  const [hx, hy, hz] = deployFoot(id);
  const ex = Math.max(0.02, hx - CLIP_DEFLATE), ez = Math.max(0.02, hz - CLIP_DEFLATE);
  const hangs = placeRule(id as CraftableId).mount?.anchor === 'top';
  const bottom = hangs ? y - Math.max(0.02, hy - CLIP_DEFLATE) : y + CLIP_SEAT_LIFT;
  const top = hangs ? y + Math.max(0.02, hy - CLIP_DEFLATE) : y + Math.max(hy * 2 - CLIP_DEFLATE, CLIP_SEAT_LIFT + 0.04);
  const cy = (bottom + top) / 2, ey = (top - bottom) / 2;
  const pr = ex + ez;
  for (const b of boxes) {
    const dx = b.cx - x, dz = b.cz - z;
    const rr = pr + b.hx + b.hz;
    if (dx * dx + dz * dz > rr * rr) continue;
    if (obbOverlap(x, cy, z, ex, ey, ez, yaw, b.cx, b.cy, b.cz, b.hx, b.hy, b.hz, b.yaw)) return true;
  }
  return false;
}

// Translucent ghost preview shown while a deployable is held. One shared green/red material; one ghost at a time.
const GHOST_OK = new THREE.MeshBasicMaterial({ color: 0x5fe06f, transparent: true, opacity: 0.5, depthWrite: false });
const GHOST_BAD = new THREE.MeshBasicMaterial({ color: 0xe05f5f, transparent: true, opacity: 0.5, depthWrite: false });

// The preview model for any deployable id -- craftables use their real model; chest/campfire reuse their own
// mesh builders (no cycle: chest/campfire don't import this module). Lights/flames are stripped for the ghost.
function ghostModel(id: DeployId): THREE.Group {
  if (id === 'chest' || id === 'bigchest') { const g = new THREE.Group(); buildChestMesh(g, id === 'bigchest' ? 'big' : 'small'); return g; }
  if (id === 'campfire') { const g = new THREE.Group(); const b = buildCampfireMesh(g); b.light.visible = false; b.glow.visible = false; b.flame.visible = false; return g; }
  return buildCraftableModel(id);
}

export class DeployGhost {
  readonly group = new THREE.Group();
  private id: DeployId | null = null;
  constructor() { this.group.visible = false; }
  set(id: DeployId): void {
    if (id === this.id) return;
    this.id = id; this.group.clear();
    const m = ghostModel(id);
    m.traverse((o) => { const me = o as THREE.Mesh; if (me.isMesh) me.material = GHOST_OK; });
    this.group.add(m);
  }
  update(x: number, y: number, z: number, yaw: number, valid: boolean): void {
    this.group.visible = true;
    this.group.position.set(x, y, z); this.group.rotation.y = yaw;
    const mat = valid ? GHOST_OK : GHOST_BAD;
    this.group.traverse((o) => { const me = o as THREE.Mesh; if (me.isMesh) me.material = mat; });
  }
  hide(): void { this.group.visible = false; }
}
